import { Button, Form, FormField, Input, ModalDialog, Numeric, Select } from "@rothoblaas/design-system";
import { FC, useContext } from "react";
import { Controller, type UseFormReturn } from "react-hook-form";
import FormFieldsContainer from "../../../../../components/ui/FormFieldsContainer";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import type { CustomMaterialGrade, CustomMaterialInput, MaterialGradeLight } from "../../../../../http/lock_calculator/types";
import { useCustomMaterialsApi } from "../../../../../http/lock_calculator/useCustomMaterials";
import { useLockCalculatorCatalog } from "../../hooks/useLockCalculatorCatalog";
import styles from "../../LockCalculator.module.css";

type FormBody = {
  name: string;
  baseGradeId: string | null;
  rhoK: number | null;
  rhoM: number | null;
  fc0K: number | null;
  fc90K: number | null;
  fvK: number | null;
  frK: number | null;
  faxK: number | null;
};

type NumberField = "rhoK" | "rhoM" | "fc0K" | "fc90K" | "fvK" | "frK" | "faxK";

const KG_PER_M3 = " kg/m³";
const N_PER_MM2 = " N/mm²";

const valuesOf = (grade?: MaterialGradeLight): Omit<FormBody, "name" | "baseGradeId"> => ({
  rhoK: grade?.rhoK ?? null,
  rhoM: grade?.rhoM ?? null,
  fc0K: grade?.fc0K ?? null,
  fc90K: grade?.fc90K ?? null,
  fvK: grade?.fvK ?? null,
  frK: grade?.frK ?? null,
  faxK: grade?.faxK ?? null,
});

interface CustomMaterialDialogProps {
  /** Set to edit that material; omitted to create one. */
  item?: CustomMaterialGrade;
  /** Grades a new material can be based on (its numbers prefill the form). */
  baseOptions: MaterialGradeLight[];
  onClose: () => void;
  onSaved: (id: string) => void;
}

/**
 * Creates or edits a material of the user's library. A new one is based on an existing grade, whose
 * category, normative and structural family it inherits (so kmod and the service-class rules keep
 * resolving); only the numbers are the user's.
 */
export const CustomMaterialDialog: FC<CustomMaterialDialogProps> = ({ item, baseOptions, onClose, onSaved }) => {
  const i18n = useI18n();
  const t = i18n.translation;
  const text = t.lockCalculatorModule.customMaterial;
  const dispatch = useContext(NotificationDispatch);
  const { isAdmin } = useLockCalculatorCatalog();
  const api = useCustomMaterialsApi(isAdmin);

  // A material cannot be its own base.
  const selectableBases = baseOptions.filter((option) => option.id !== item?.id);

  const defaultValues: FormBody = {
    name: item?.description ?? "",
    baseGradeId: null,
    ...valuesOf(item),
  };

  const submit = () => async (form: FormBody) => {
    const body: CustomMaterialInput = {
      name: form.name,
      baseGradeId: form.baseGradeId ?? undefined,
      rhoK: form.rhoK as number,
      rhoM: form.rhoM,
      fc0K: form.fc0K as number,
      fc90K: form.fc90K as number,
      fvK: form.fvK as number,
      frK: form.frK as number,
      faxK: form.faxK,
    };
    try {
      const saved = item ? await api.update(item.id, body) : await api.create(body);
      dispatch(showSuccessNotification(t));
      onSaved(saved.id);
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const numberField = (
    form: UseFormReturn<FormBody>,
    name: NumberField,
    label: string,
    suffix: string,
    required: boolean,
  ) => (
    <Controller
      name={name}
      control={form.control}
      rules={required ? { validate: (value) => (value != null && value > 0) || false } : { min: 0 }}
      render={({ field, fieldState }) => (
        <FormField label={label} error={fieldState.error} isOptional={!required}>
          {({ labelId, isInvalid }) => (
            <Numeric
              id={labelId}
              value={field.value ?? undefined}
              suffix={suffix}
              decimalScale={name === "rhoK" || name === "rhoM" ? 0 : 2}
              allowNegative={false}
              isInvalid={isInvalid}
              onValueChange={(value) => field.onChange(value ?? null)}
            />
          )}
        </FormField>
      )}
    />
  );

  return (
    <ModalDialog onClose={onClose} title={item ? text.edit : text.create}>
      <Form<FormBody>
        submitText={t.common.save}
        onSubmit={submit}
        defaultValues={defaultValues}
        secondaryButton={
          <Button onClick={onClose} variant="secondary">
            {t.common.cancel}
          </Button>
        }
      >
        {(form) => {
          const baseId = form.watch("baseGradeId");
          const base = selectableBases.find((option) => option.id === baseId);
          // What the material inherits: the chosen base, else (editing, base unchanged) itself.
          const inheritedFrom = base ?? item;

          return (
            <FormFieldsContainer fullWidth>
              <Controller
                name="name"
                control={form.control}
                rules={{ required: true, validate: (value) => value.trim().length > 0 }}
                render={({ field, fieldState }) => (
                  <FormField label={text.name} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Input id={labelId} value={field.value} isInvalid={isInvalid} onChange={field.onChange} />
                    )}
                  </FormField>
                )}
              />

              <Controller
                name="baseGradeId"
                control={form.control}
                rules={{ required: !item }}
                render={({ field, fieldState }) => (
                  <FormField label={text.baseGrade} error={fieldState.error} isOptional={!!item}>
                    {({ labelId, isInvalid }) => (
                      <Select<MaterialGradeLight, false>
                        inputId={labelId}
                        value={selectableBases.find((option) => option.id === field.value) ?? null}
                        options={selectableBases}
                        getOptionValue={(option) => option.id}
                        getOptionLabel={(option) =>
                          option.isCustom ? `${option.description} (${text.badge})` : option.description
                        }
                        placeholder={item ? text.keepCurrentBase : t.lockCalculatorModule.selectPlaceholder}
                        isClearable={!!item}
                        isInvalid={isInvalid}
                        onChange={(selected) => {
                          field.onChange(selected?.id ?? null);
                          if (!selected) return;
                          // Start from the base's numbers; they stay editable.
                          const prefill = valuesOf(selected);
                          (Object.keys(prefill) as NumberField[]).forEach((key) => form.setValue(key, prefill[key]));
                        }}
                      />
                    )}
                  </FormField>
                )}
              />

              {inheritedFrom && (
                <FormField label={text.inherited}>
                  <div className={styles.metricList}>
                    <div>
                      <span>{text.family}</span>
                      <strong>{inheritedFrom.structuralFamily ?? "—"}</strong>
                    </div>
                    <div>
                      <span>{text.category}</span>
                      <strong>{inheritedFrom.categoryName || "—"}</strong>
                    </div>
                    <div>
                      <span>{text.normative}</span>
                      <strong>{inheritedFrom.normativeCode || "—"}</strong>
                    </div>
                  </div>
                </FormField>
              )}

              {numberField(form, "rhoK", text.rhoK, KG_PER_M3, true)}
              {numberField(form, "fc0K", text.fc0K, N_PER_MM2, true)}
              {numberField(form, "fc90K", text.fc90K, N_PER_MM2, true)}
              {numberField(form, "fvK", text.fvK, N_PER_MM2, true)}
              {numberField(form, "frK", text.frK, N_PER_MM2, true)}
              <FormField label={text.advanced} isOptional>
                <FormFieldsContainer fullWidth>
                  {numberField(form, "rhoM", text.rhoM, KG_PER_M3, false)}
                  {numberField(form, "faxK", text.faxK, N_PER_MM2, false)}
                </FormFieldsContainer>
              </FormField>

              {!!item && item.usageCount > 0 && <p>{text.usedInCalculations(item.usageCount)}</p>}
            </FormFieldsContainer>
          );
        }}
      </Form>
    </ModalDialog>
  );
};
